import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { countryService } from "../services/countryService"; 
import { CountryState } from "../models/state/countryState"; 
import { CountryModel } from "../models/models/countryModel";
import { CountryResource } from "../models/resource/countryResource";
import { CountryRequest } from "../models/request/countryRequest";
import { CountryResponse } from "../models/response/countryResponse";

const initialState: CountryState = {
  countries: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
  countriesGrid: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
  country: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
};

export const getCountries = createAsyncThunk<
  CountryModel,
  void,
  { rejectValue: string }
>("country/getCountries", async (_, { rejectWithValue }) => {
  try {
    const response = await countryService.getCountries();
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const getCountryById = createAsyncThunk<
  CountryModel,
  { id: number },
  { rejectValue: string }
>("country/getCountryById", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await countryService.getCountryById(id);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const deleteCountry = createAsyncThunk<
CountryResponse,
  { data: number },
  { rejectValue: string }
>(
  "offerIssuer/deleteCountry",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await countryService.deleteCountry(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
);  

export const saveCountry = createAsyncThunk<
  any,
  { data: CountryResource },
  { rejectValue: string }
>("country/saveCountry", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await countryService.saveCountry(data);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const getCountriesList = createAsyncThunk<
  CountryResponse,
  { data: CountryRequest },
  { rejectValue: string }
>("country/getCountriesList", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await countryService.getCountriesList(data);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
}); 

const countryReducer = createSlice({
  name: "country",
  initialState,
  reducers: {
    clearBlobData: (state) => {
    state.countriesGrid.blobData = null;
  }},
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.pending, (state) => {
        state.countries.status = "loading";
        state.countries.isLoading = true;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.countries.status = "idle";
        state.countries.data = action.payload;
        state.countries.isLoading = false;
      })
      .addCase(getCountries.rejected, (state, action) => {
        state.countries.status = "failed";
        state.countries.isLoading = false;
        if (action.payload) {
          state.countries.error = action.payload;
          state.countries.message = action.payload;
        } else {
          state.countries.message = "An unexpected error occurred";
        }
      }) 
      .addCase(saveCountry.pending, (state) => {
        state.countries.status = "loading";
        state.countries.isLoading = true;
      })
      .addCase(saveCountry.fulfilled, (state, action) => {
        state.countries.status = "idle";
        state.countries.data = action.payload;
        state.countries.isLoading = false;
      })
      .addCase(saveCountry.rejected, (state, action) => {
        state.countries.status = "failed";
        state.countries.isLoading = false;
        if (action.payload) {
          state.countries.error = action.payload;
          state.countries.message = action.payload;
        } else {
          state.countries.message = "An unexpected error occurred";
        }
      })
      .addCase(getCountryById.pending, (state) => {
        state.country.status = "loading";
        state.country.isLoading = true;
      })
      .addCase(getCountryById.fulfilled, (state, action) => {
        state.country.status = "idle";
        state.country.data = action.payload;
        state.country.isLoading = false;
      })
      .addCase(getCountryById.rejected, (state, action) => {
        state.country.status = "failed";
        state.country.isLoading = false;
        if (action.payload) {
          state.country.error = action.payload;
          state.country.message = action.payload;
        } else {
          state.country.message = "An unexpected error occurred";
        }
      })
      .addCase(getCountriesList.pending, (state) => {
        state.countriesGrid.status = "loading";
        state.countriesGrid.isLoading = true;
      })
      .addCase(getCountriesList.fulfilled, (state, action) => {
        state.countriesGrid.status = "idle";
        state.countriesGrid.data = action.payload;
        state.countriesGrid.isLoading = false;
      })
      .addCase(getCountriesList.rejected, (state, action) => {
        state.countriesGrid.status = "failed";
        state.countriesGrid.isLoading = false;
        if (action.payload) {
          state.countriesGrid.error = action.payload;
          state.countriesGrid.message = action.payload;
        } else {
          state.countriesGrid.message = "An unexpected error occurred";
        }
      }) 
      .addCase(deleteCountry.pending, (state) => {
        state.countriesGrid.status = "loading";
      })
      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.countriesGrid.status = "idle";
        state.countriesGrid.data = action.payload;
        state.countriesGrid.isLoading = false;
      })
      .addCase(deleteCountry.rejected, (state, action) => {
        state.countriesGrid.status = "failed";
        state.countriesGrid.isLoading = false;
        if (action.payload) {
          state.countriesGrid.error = action.payload;
          state.countriesGrid.message = action.payload;
        } else {
          state.countriesGrid.message = "An unexpected error occurred";
        }
      });
  },
});

export default countryReducer.reducer;
export const { clearBlobData } = countryReducer.actions;